import {
  actionCloseSelectOperator,
  actionOpenLaborTicketModal,
  actionOpenSelectOperator,
  actionSetEmbeddedLaborTicket,
} from 'lib/actions';
import { getEmbeddedLaborTicketFlowEnabled } from 'lib/selectors/getEmbeddedLaborTicketFlowEnabled';
import { getCurrentLaborTicket } from 'lib/selectors/getLaborTickets';
import {
  getSelectOperatorEnabled,
  getSelectOperatorOpen,
  getSelectOperatorOptions,
} from 'lib/selectors/getSelectOperator';
import { getUnattendedOperatorPersonId } from 'lib/selectors/getUnattendedOperatorPersonId';
import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

/**
 * Custom hook to manage the operator selection modal state and actions.
 * Provides functionality to open/close the operator selection modal and handles
 * redirection after selection.
 *
 * @returns {Object} An object containing:
 *   - selectOperatorOpen: boolean indicating if the modal is open
 *   - selectOperatorOptions: object containing modal configuration options
 *   - openSelectOperator: function to open the operator selection modal
 *   - closeSelectOperator: function to close the modal and handle redirection
 */
const useSelectOperator = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const selectOperatorOpen = useSelector(getSelectOperatorOpen);
  const selectOperatorOptions = useSelector(getSelectOperatorOptions);
  const selectOperatorEnabled = useSelector(getSelectOperatorEnabled);
  const isEmbeddedLaborFlowEnabled = useSelector(
    getEmbeddedLaborTicketFlowEnabled
  );
  const unattendedOperatorPersonId = useSelector(getUnattendedOperatorPersonId);
  const currentLaborTicket = useSelector(getCurrentLaborTicket);
  const isCurrentTicketUnattended =
    currentLaborTicket?.personId === unattendedOperatorPersonId;

  /**
   * Opens the operator selection modal with the provided options
   * @param {Object} options - Configuration options for the modal
   * @returns {Object} The dispatched action
   */
  const openSelectOperator = useCallback(
    (options = {}) => {
      if (isEmbeddedLaborFlowEnabled && isCurrentTicketUnattended) {
        dispatch(actionSetEmbeddedLaborTicket(currentLaborTicket));
        return dispatch(actionOpenLaborTicketModal());
      }

      return dispatch(
        actionOpenSelectOperator({
          ...options,
        })
      );
    },
    [
      dispatch,
      isEmbeddedLaborFlowEnabled,
      isCurrentTicketUnattended,
      currentLaborTicket,
    ]
  );

  /**
   * Closes the operator selection modal and handles redirection if needed
   * @param {string} [redirectPath] - Optional path to redirect to after closing
   * @returns {Object} The dispatched action
   */
  const closeSelectOperator = useCallback(
    (redirectPath) => {
      const path = redirectPath || selectOperatorOptions?.redirectPath;

      if (path) {
        history.push(path);
      }

      return dispatch(actionCloseSelectOperator());
    },
    [dispatch, history, selectOperatorOptions]
  );

  // Automatically close the modal if it becomes disabled while open
  useEffect(() => {
    if (selectOperatorOpen && !selectOperatorEnabled) {
      dispatch(actionCloseSelectOperator());
    }
  }, [selectOperatorOpen, selectOperatorEnabled, dispatch]);

  return {
    selectOperatorOpen,
    selectOperatorOptions,
    openSelectOperator,
    closeSelectOperator,
  };
};

export default useSelectOperator;
